import { Avatar, Paper, Text, Button, Divider, TextInput, Stack, createStyles, Flex, Tooltip } from "@mantine/core";
import { observer } from "mobx-react";
import { useRef, useState } from "react";
import t from "../../stores/language";
import { useForm } from "@mantine/form";
import { useQuery, useStore } from "../../stores/store";
import { useParams } from "react-router-dom";
import { delay } from "./module-assumptions";
import theme from "../../stores/theme";
import ImageUpload, { ImageUploadHandle } from "../../custom-components/image-upload";
import { QueryKeys } from "../../models/models";
import { Icon } from "../../custom-components/icons";
import { faUpload } from "@fortawesome/pro-duotone-svg-icons";
import { getUserTitle, imageLoader } from "../top-menu";

const useStyles = createStyles((theme) => ({
  profileContainer: {
    display: "flex",
    justifyContent: "center",
    minHeight: "80vh",
    alignItems: "center",
  },
  profileCard: {
    boxShadow: theme.other.arc.shadows.lg,
    padding: theme.other.arc.paddings[3],
    width: "50%",
    borderRadius: "0rem",
  },
  profileImage: {
    marginLeft: "auto",
    marginRight: "auto",
    width: "7.5rem",
    minWidth: "7.5rem",
    height: "7.5rem",
    borderRadius: "7.5rem",
    ".mantine-Avatar-placeholder": {
      borderRadius: "7.5rem",
      fontSize: "calc(7.5rem / 2.5)",
      color: theme.other.arc.colors[2],
      backgroundColor: "#e7f5ff",
    },
  },
  profileInputsContainer: {
    justifyContent: "center",
    gap: theme.other.arc.paddings[4],
    marginTop: theme.other.arc.margins[2],
  },
  iconButton: {
    "&: hover": {
      backgroundColor: "transparent",
    },
  },
}));

const Profile = observer(function Profile() {
  const { authStore } = useStore();
  const { userId } = useParams();
  const { classes } = useStyles();

  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const profileUploadRef = useRef<ImageUploadHandle>(null);

  const { data: user, isLoading: loading } = useQuery({
    queryKey: [QueryKeys.User],
    queryFn: () => {
      return authStore.GetUserById(userId ?? "1");
    },
    onSuccess: (data) => {
      form.setValues({ email: data?.email ?? "", name: data?.name ?? "" });
    },
  });

  useQuery({
    queryKey: [QueryKeys.ProfileImage],
    queryFn: () => {
      return authStore.GetProfileImageUrl(userId ?? "1");
    },
    onSuccess: (data) => {
      (async () => {
        try {
          const image = await imageLoader(data);
          setProfileImage(image);
        } catch {
          setProfileImage(null);
        }
      })();
    },
  });

  const form = useForm({
    initialValues: {
      email: user?.email ?? "",
      name: user?.name ?? "",
    },
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      name: (value) => (value !== "" ? null : "Name is required"),
    },
  });

  const resetState = () => {
    if (error.message !== "") {
      setError({ isError: false, message: "" });
    }
  };

  const updateUser = async (values: typeof form.values) => {
    if (user == null) {
      return;
    }
    setSaving(true);
    const result = await authStore.UpdateUser({ ...values, lockoutEnabled: true, id: user.id ?? "1", userType: user.userType });
    if (!result.error) {
      setError({ isError: false, message: `User Updated Successfully` });
      await delay(300);
      setSaving(false);
      setError({ isError: false, message: "" });
    } else {
      setError({ isError: true, message: (result.data as string[]).toLocaleString() });
      setSaving(false);
    }
  };

  const uploadProfileImage = async (file: File) => {
    const result = await authStore.UpdateUserProfileImage(file, userId ?? "1");
    return result;
  };

  return loading ? (
    <></>
  ) : (
    <div className={classes.profileContainer}>
      <Paper radius={0} withBorder className={classes.profileCard}>
        <Avatar className={classes.profileImage} src={profileImage}>
          {getUserTitle(user?.name ?? "")}
        </Avatar>
        <Flex className={classes.profileInputsContainer}>
          <Tooltip label={t("Change Profile Image")} withArrow withinPortal>
            <Button
              size="xs"
              onClick={() => {
                profileUploadRef.current?.show();
              }}
              variant="subtle"
              compact
              className={classes.iconButton}
            >
              <Icon icon={faUpload} size="xl" />
            </Button>
          </Tooltip>
        </Flex>
        <Divider label={user?.userType === 1 ? "Admin" : user?.userType === 0 ? "Premium" : "Basic"} labelPosition="center" my="xs" />
        <form onSubmit={form.onSubmit(updateUser)}>
          <Stack>
            <TextInput
              label={t("Name")}
              placeholder={t("name")}
              {...form.getInputProps("name")}
              onChange={(event) => {
                form.setFieldValue("name", event.currentTarget.value);
                resetState();
              }}
              withAsterisk
            />
            <TextInput
              required
              label="Email"
              placeholder={t("Email")}
              {...form.getInputProps("email")}
              onChange={(event) => {
                form.setFieldValue("email", event.currentTarget.value);
                resetState();
              }}
              withAsterisk
            />
            {error.message !== "" ? (
              <Text size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"} align="right">
                {error.message}
              </Text>
            ) : null}
            <Button type="submit" loading={saving} m={"auto"}>
              {t("Save")}
            </Button>
          </Stack>
        </form>
        <ImageUpload ref={profileUploadRef} title={t("Upload Profile Image")} onUpload={uploadProfileImage} />
      </Paper>
    </div>
  );
});

export default Profile;
